import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaBell, FaMoneyBillWave } from "react-icons/fa";
import moment from "moment";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetTreatmentComponent({ appointmentId, doctorId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petTreatments, setPetTreatments] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTreatment, setEditingTreatment] = useState(null);
    const [selectedTreatment, setSelectedTreatment] = useState("");
    const [treatmentPrice, setTreatmentPrice] = useState(0);
    const [recommendation, setRecommendation] = useState("");

    const getTodayDateTimeLocal = () => {
        const now = new Date();
        const offset = now.getTimezoneOffset();
        const local = new Date(now.getTime() - offset * 60000);
        return local.toISOString().slice(0, 16);
    };

    const [treatmentDate, setTreatmentDate] = useState(getTodayDateTimeLocal());
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [hasTreatmentForAppointment, setHasTreatmentForAppointment] = useState(false);

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return "0";

        const num = parseFloat(price);

        if (isNaN(num)) return "0";

        return Number.isInteger(num) ? num.toString() : num.toFixed(2);
    };

    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    useEffect(() => {
        fetchPetTreatments();
    }, [appointmentId, petId]);

    const fetchPetTreatments = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-treatment/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const treatmentsForPet = response.data.pet_treatments || [];
                setPetTreatments(treatmentsForPet);
                setTreatments(response.data.treatments || []);
                setHasTreatmentForAppointment(
                    treatmentsForPet.some(item =>
                        String(item.appointment_id) === String(appointmentId) ||
                        String(item.appointment?.id) === String(appointmentId)
                    )
                );
            }
        } catch (error) {
            console.error("Error fetching pet treatments:", error);
            showErrorToast(t('failed to load treatments'));
            setPetTreatments([]);
            setTreatments([]);
        } finally {
            setLoading(false);
        }
    };

    const handleTreatmentSelect = (selected) => {
        const treatmentId = selected?.value;
        setSelectedTreatment(treatmentId || "");

        const treatmentItem = treatments.find(item => item.id === treatmentId);
        if (treatmentItem) {
            setTreatmentPrice(treatmentItem.price || 0);
        } else {
            setTreatmentPrice(0);
        }
    };

    const handleAddTreatment = () => {
        setEditingTreatment(null);
        setSelectedTreatment("");
        setTreatmentPrice(0);
        setRecommendation("");
        setTreatmentDate(getTodayDateTimeLocal());
        setGeneralNote("");
        setExistingNoteId(null);
        setShowModal(true);
    };

    const handleEditTreatment = (petTreatment) => {
        setEditingTreatment(petTreatment);
        setSelectedTreatment(petTreatment.treatment_id || (petTreatment.treatment?.id ?? ""));
        setTreatmentPrice(petTreatment.price || 0);
        setRecommendation(petTreatment.recomendation || "");
        setTreatmentDate(petTreatment.date ? petTreatment.date.substring(0, 16) : "");

        if (petTreatment.note && Array.isArray(petTreatment.note) && petTreatment.note.length > 0) {
            setGeneralNote(petTreatment.note[0].note || "");
            setExistingNoteId(petTreatment.note[0].id);
        } else if (petTreatment.note && typeof petTreatment.note === 'object') {
            setGeneralNote(petTreatment.note.note || "");
            setExistingNoteId(petTreatment.note.id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setShowModal(true);
    };

    const handleDeleteClick = (treatmentId) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const AuthToken = localStorage.getItem("authToken");
                    const response = await axios.delete(
                        `${process.env.REACT_APP_API_URL}/pet-treatment-delete/${treatmentId}`,
                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                    );

                    if (response.data) {
                        fetchPetTreatments();
                        showSuccessToast(t('Treatment deleted successfully!'));
                        return true;
                    }
                } catch (error) {
                    console.error("Error deleting treatment:", error);
                    showErrorToast(t('failed to delete treatment'));
                    return false;
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedTreatment) {
            showErrorToast(t('please select treatment'));
            return;
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingTreatment
                ? `${process.env.REACT_APP_API_URL}/pet-treatment-update/${editingTreatment.id}`
                : `${process.env.REACT_APP_API_URL}/pet-treatment`;

            const payload = {
                appointment_id: appointmentId,
                treatment_id: selectedTreatment ? parseInt(selectedTreatment) : null,
                price: treatmentPrice || null,
                recomendation: recommendation || null,
                date: treatmentDate || null,
            };

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                showSuccessToast(editingTreatment ? t('Treatment updated successfully!') : t('Treatment added successfully!'));
                fetchPetTreatments();
            }
        } catch (error) {
            console.error("Error saving treatment:", error);
            showErrorToast(error.response?.data?.message || t('failed to save treatment'));
        } finally {
            setSubmitting(false);
        }
    };

    const getTreatmentOptions = () => {
        return treatments.map(item => ({
            value: item.id,
            label: `${item.name}`,
            price: item.price,
        }));
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#e0e0e0' : 'white',
            color: '#333',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const groupTreatmentsByAppointment = () => {
        const grouped = {};
        petTreatments.forEach(pt => {
            const apptId = pt.appointment_id;
            if (!grouped[apptId]) {
                grouped[apptId] = {
                    appointment: pt.appointment,
                    treatments: []
                };
            }
            grouped[apptId].treatments.push(pt);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading treatments')}</p>
            </div>
        );
    }

    const treatmentOptions = getTreatmentOptions();
    const selectedOption = treatmentOptions.find(opt => opt.value === selectedTreatment);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('treatments list')}</h6>
                {isAppointmentOpen && !hasTreatmentForAppointment && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddTreatment}
                        className="d-flex align-items-center gap-2"
                    >
                        <FaPlus size={12} /> {t('add treatment')}
                    </Button>
                )}
            </div>

            {!petTreatments || petTreatments.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaStethoscope size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no treatments found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key === openAccordion ? null : key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupTreatmentsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, treatments: appointmentTreatments } = appointmentData;

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>

                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentTreatments.length} {t('treatment(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key === openNestedAccordion[appointmentIdKey] ? null : key
                                        })}
                                        className="nested-checkups-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentTreatments.map((pt) => {
                                            if (!pt) return null;
                                            const dateTime = pt.date ? formatDateTime(pt.date) : null;
                                            const noteText = pt.note && Array.isArray(pt.note) && pt.note.length > 0
                                                ? pt.note[0].note
                                                : (pt.note?.note || null);

                                            return (
                                                <Accordion.Item eventKey={String(pt.id)} key={pt.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                                                <Badge bg="info" className="px-3 py-2">
                                                                    {pt.treatment?.name || 'N/A'}
                                                                </Badge>
                                                                <div className="text-muted small">
                                                                    👨‍⚕️ <strong>Doctor:</strong> {pt?.doctor?.name || pt?.doctor?.name || 'Unknown Doctor'}
                                                                </div>
                                                                {dateTime && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <FaCalendarAlt size={14} />
                                                                            {moment(pt.date).format("DD MMM YYYY")}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <FaBell size={14} />
                                                                            {moment(pt.date, "HH:mm:ss").format("hh:mm A")}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                                                                <Button
                                                                    variant="primary"
                                                                    size="sm"
                                                                    onClick={() => handleEditTreatment(pt)}
                                                                    className="d-flex align-items-center gap-1"
                                                                >
                                                                    <FaEdit size={12} />
                                                                    <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                </Button>
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteClick(pt.id)}
                                                                    className="d-flex align-items-center gap-1"
                                                                >
                                                                    <FaTrash size={12} />
                                                                    <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col md={12}>
                                                                <div className="border-bottom pb-2 mb-3">
                                                                    <h6 className="mb-0 d-flex align-items-center gap-2">
                                                                        <FaStethoscope className="text-primary" />
                                                                        {t('treatment details')}
                                                                    </h6>
                                                                </div>
                                                                <div className="p-3 bg-light rounded">
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Treatment Name')}:</strong>{' '}
                                                                        {pt.treatment?.name || 'N/A'}
                                                                    </div>
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Price')}:</strong>{' '}
                                                                        <FaMoneyBillWave className="text-success me-1" />
                                                                        {formatPrice(pt.price)}
                                                                    </div>
                                                                   
                                                                    {pt.date && (
                                                                        <div className="mb-2">
                                                                            <strong className="text-muted">{t('Treatment Date')}:</strong>{' '}
                                                                            {moment(pt.date).format("DD MMM YYYY hh:mm A")}
                                                                        </div>
                                                                    )}
                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>
                        {editingTreatment ? t('Edit Treatment') : t('Add Treatment')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Select Treatment')} <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={treatmentOptions}
                                value={selectedOption}
                                onChange={handleTreatmentSelect}
                                placeholder={t('select treatment')}
                                isSearchable
                                styles={customSelectStyles}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Price')}</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={formatPrice(treatmentPrice)}
                                onChange={(e) => setTreatmentPrice(e.target.value)}
                            />
                        </Form.Group>

                     

                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">{t('Treatment Date')}</Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={treatmentDate}
                                        onChange={(e) => setTreatmentDate(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('Note')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
                            {t('Close')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <Spinner animation="border" size="sm" /> : t('Save Changes')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}